"use client";
import React, { useState } from "react";
import Link from "next/link";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage("Voer een e-mailadres in.");
      setIsSuccess(false);
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setMessage("Voer een geldig e-mailadres in.");
      setIsSuccess(false);
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await fetch("/api/auth/password/reset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(
          "Een e-mail met instructies voor het resetten van je wachtwoord is verzonden naar je e-mailadres."
        );
        setIsSuccess(true);
      } else {
        if (data.email) {
          const errorMsg = data.email[0];
          // Translate common English error messages to Dutch
          if (errorMsg.includes("This field may not be blank") || errorMsg.includes("This field is required")) {
            setMessage("Voer een e-mailadres in.");
          } else if (errorMsg.includes("Enter a valid email address")) {
            setMessage("Voer een geldig e-mailadres in.");
          } else if (errorMsg.includes("No active account found")) {
            setMessage("Er is geen account gevonden met dit e-mailadres.");
          } else {
            setMessage(errorMsg || "Ongeldig e-mailadres.");
          }
        } else {
          setMessage(
            data.detail ||
              "Er is een fout opgetreden. Controleer je e-mailadres en probeer opnieuw."
          );
        }
        setIsSuccess(false);
      }
    } catch {
      setMessage("Er is een netwerkfout opgetreden. Probeer het later opnieuw.");
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-fixed bg-cover bg-no-repeat min-h-screen w-full flex items-center justify-center text-white relative"
      style={{ backgroundImage: "url('/images/inlog_achtergrond.jpg')" }}
    >
      <div className="flex items-center justify-center bg-primary_200 rounded-2xl bg-opacity-90">
        <div className="p-8 rounded-lg shadow-md w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-center">
            Wachtwoord vergeten?
          </h2>
          
          {!isSuccess ? (
            <>
              <p className="text-gray-600 mb-6 text-sm">
                Voer je e-mailadres in en we sturen je instructies om je wachtwoord te resetten.
              </p>
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label
                    className="block text-gray-500 text-sm font-bold mb-2"
                    htmlFor="email"
                  >
                    E-mailadres
                  </label>
                  <input
                    type="text"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="inputfield"
                    placeholder="voorbeeld@voorbeeld.nl"
                  />
                </div>
                
                <div className="flex flex-col space-y-4">
                  <button
                    type="submit"
                    className="button button-secondary w-full"
                    disabled={loading}
                  >
                    {loading ? "Bezig..." : "Verstuur reset-instructies"}
                  </button>
                  
                  <Link
                    href="/login"
                    className="text-center text-secondary_600 hover:text-secondary_500 text-sm"
                  >
                    Terug naar inloggen
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-green-700 mb-6 text-sm">
                {message}
              </p>
              <p className="text-gray-600 mb-6 text-sm">
                Controleer je inbox en spam-map voor de reset-instructies.
              </p>
              <Link
                href="/login"
                className="button button-secondary inline-block"
              >
                Terug naar inloggen
              </Link>
            </div>
          )}

          {message && !isSuccess && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}